import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { GridColDef, GridSortModel } from '@mui/x-data-grid';
import { type ContextMenuParams, CrudListPage } from '@core/components';
import { useAppLayout } from '@core/contexts/LayoutContext';
import { useCrudListPage } from '@core/hooks/useCrudListPage';
import { getOptions } from '@core/utils/enum';
import {
  type Order,
  type OrderQuery,
  confirmOrderReprint,
  deleteOrder,
  getOrdersPage,
} from '../../api/order';
import CancelOrderDialog from '../components/CancelOrderDialog';
import { DELETE_ORDER_DIALOG } from '../components/DeleteOrderDialog';
import MoveToProductionDialog from '../components/MoveToProductionDialog';
import OrderDetailsCell from '../components/OrderDetailsCell';
import OrderDialog from '../components/OrderDialog';
import OrderDueDateCell from '../components/OrderDueDateCell';
import OrderFilterDrawer from '../components/OrderFilterDrawer';
import OrderRowDetails from '../components/OrderRowDetails';
import OrderStatusCell from '../components/OrderStatusCell';
import ReleaseOrderDialog from '../components/ReleaseOrderDialog';
import UndoCancelDialog from '../components/UndoCancelDialog';
import UndoReleaseDialog from '../components/UndoReleaseDialog';
import { useJobOrderPrint } from '../components/useJobOrderPrint';
import { OrderStatus } from '../enum/orderStatus';
import { navItems } from '../navigation';
import { getFullName } from '../utils/customer';
import {
  EMPTY_ORDER_FILTERS,
  type OrderFilters,
  type OrderStatusFilter,
  canCancelOrder,
  canDeleteOrder,
  canUndoCancelOrder,
  canUndoReleaseOrder,
  countActiveOrderFilters,
  getOrderProgressPercent,
  orderFiltersToQueryParams,
} from '../utils/order';

const ORDER_STATUS_CHIP_OPTIONS = [
  { value: 'open', label: 'Open' },
  { value: 'closed', label: 'Closed' },
  ...getOptions(OrderStatus),
];

const Orders = () => {
  const navigate = useNavigate();
  const { isWebTwo } = useAppLayout();
  const [createOrderOpen, setCreateOrderOpen] = useState(false);
  const [orderToMove, setOrderToMove] = useState<Order | null>(null);
  const [orderToRelease, setOrderToRelease] = useState<Order | null>(null);
  const [orderToUndoRelease, setOrderToUndoRelease] = useState<Order | null>(
    null
  );
  const [orderToCancel, setOrderToCancel] = useState<Order | null>(null);
  const [orderToUndoCancel, setOrderToUndoCancel] = useState<Order | null>(
    null
  );
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const { isPrintAvailable, printJobOrders, printView, attestationDialog } =
    useJobOrderPrint();
  const toQueryParams = useCallback(
    (searchTerm: string, filters: OrderFilters): OrderQuery => {
      const query = orderFiltersToQueryParams(searchTerm, filters);
      const activeSort = sortModel.find((item) => item.sort);
      if (activeSort?.sort) {
        query.sort = activeSort.field;
        query.direction = activeSort.sort;
      }
      return query;
    },
    [sortModel]
  );
  const {
    filteredItems: filteredOrders,
    loading,
    setSearchTerm,
    filters,
    setFilters,
    clearFilters,
    filterOpen,
    openFilters,
    closeFilters,
    activeFilterCount,
    deleteConfirm,
    load,
    requestDelete,
    cancelDelete,
    confirmDelete,
    deleteLoading,
    getRowId,
    findByRowId,
    paginationMode,
    rowCount,
    paginationModel,
    onPaginationModelChange,
    pageSizeOptions,
  } = useCrudListPage<Order, OrderFilters, OrderQuery>({
    loadPage: getOrdersPage,
    toQueryParams,
    deleteItem: (order) => deleteOrder(order.id),
    loadErrorMessage: 'Failed to load orders',
    deleteSuccessMessage: 'Order deleted',
    deleteErrorMessage: 'Failed to delete order',
    filter: {
      empty: EMPTY_ORDER_FILTERS,
      countActive: countActiveOrderFilters,
    },
  });
  const onSortModelChange = useCallback((model: GridSortModel) => {
    setSortModel(model.filter((item) => item.sort));
  }, []);

  const contextMenuItems = useCallback(
    ({ rowId }: ContextMenuParams) => {
      const order = findByRowId(rowId);
      if (!order) return [];
      return [
        {
          label: 'View',
          onClick: () => {
            navigate(`/orders/${order.id}`);
          },
        },
        // ...(canEditOrder(order.status)
        //   ? [
        //       {
        //         label: 'Edit',
        //         onClick: () => {
        //           navigate(`/orders/${order.id}/edit-order`);
        //         },
        //       },
        //     ]
        //   : []),
        ...(order.status === OrderStatus.ReadyForProduction
          ? [
              {
                label: 'Move to Production',
                onClick: () => {
                  setOrderToMove(order);
                },
              },
            ]
          : []),
        ...(isPrintAvailable && order.status === OrderStatus.InProduction
          ? [
              {
                label: 'Reprint',
                onClick: () => {
                  const itemIds = order.items.map((item) => item.id);
                  printJobOrders({
                    input: order,
                    onConfirm: async () => {
                      await confirmOrderReprint(order.id, itemIds);
                      load();
                    },
                  });
                },
              },
            ]
          : []),
        ...(order.status === OrderStatus.ReadyForRelease
          ? [
              {
                label: 'Release',
                onClick: () => {
                  setOrderToRelease(order);
                },
              },
            ]
          : []),
        ...(canUndoReleaseOrder(order.status)
          ? [
              {
                label: 'Undo release',
                onClick: () => {
                  setOrderToUndoRelease(order);
                },
              },
            ]
          : []),
        ...(canCancelOrder(order.status)
          ? [
              {
                label: 'Cancel',
                onClick: () => {
                  setOrderToCancel(order);
                },
                destructive: true,
              },
            ]
          : []),
        ...(canUndoCancelOrder(order.status)
          ? [
              {
                label: 'Undo cancel',
                onClick: () => {
                  setOrderToUndoCancel(order);
                },
              },
            ]
          : []),
        ...(canDeleteOrder(order.status)
          ? [
              {
                label: 'Delete',
                onClick: () => {
                  requestDelete(order);
                },
                destructive: true,
              },
            ]
          : []),
      ];
    },
    [
      findByRowId,
      navigate,
      isPrintAvailable,
      printJobOrders,
      load,
      requestDelete,
    ]
  );

  const columns = useMemo<GridColDef<Order>[]>(
    () => [
      {
        field: 'details',
        headerName: 'Details',
        flex: 1,
        minWidth: 180,
        valueGetter: (_, row) => getFullName(row.customer),
        renderCell: ({ row }) => <OrderDetailsCell order={row} />,
      },
      {
        field: 'status',
        headerName: 'Status',
        flex: 1,
        minWidth: 180,
        valueGetter: (_, row) => getOrderProgressPercent(row),
        renderCell: ({ row }) => <OrderStatusCell order={row} />,
      },
      {
        field: 'dueDate',
        headerName: 'Due Date',
        flex: 1,
        minWidth: 180,
        renderCell: ({ row }) => <OrderDueDateCell order={row} />,
      },
    ],
    []
  );

  const getRowDetails = useCallback(
    (order: Order) => <OrderRowDetails order={order} onUpdated={load} />,
    [load]
  );

  const onRowClick = useCallback(
    ({ row }: { row: Order }) => {
      navigate(`/orders/${row.id}`);
    },
    [navigate]
  );

  return (
    <>
      <CrudListPage<Order>
        navItems={navItems}
        path="/orders"
        rows={filteredOrders}
        columns={columns}
        loading={loading}
        getRowId={getRowId}
        getRowDetails={!isWebTwo ? getRowDetails : undefined}
        onRowClick={isWebTwo ? onRowClick : undefined}
        contextMenuItems={contextMenuItems}
        paginationMode={paginationMode}
        rowCount={rowCount}
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
        pageSizeOptions={pageSizeOptions}
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}
        searchConfig={{
          placeholder: 'Search orders...',
          onSearch: setSearchTerm,
        }}
        filterChips={{
          options: ORDER_STATUS_CHIP_OPTIONS,
          value: filters.status,
          onChange: (value) =>
            setFilters((prev) => ({
              ...prev,
              status: value as OrderStatusFilter,
            })),
        }}
        filterConfig={{
          activeCount: activeFilterCount,
          onOpen: openFilters,
        }}
        addConfig={{
          label: 'Add Order',
          onAdd: () => setCreateOrderOpen(true),
        }}
        deleteConfig={{
          title: DELETE_ORDER_DIALOG.title,
          item: deleteConfirm,
          getContent: () => DELETE_ORDER_DIALOG.content,
          onConfirm: confirmDelete,
          onCancel: cancelDelete,
          loading: deleteLoading,
        }}
        rowHeight={64}
      />
      <OrderFilterDrawer
        open={filterOpen}
        filters={filters}
        onApply={setFilters}
        onClear={clearFilters}
        onClose={closeFilters}
      />
      <OrderDialog
        open={createOrderOpen}
        onClose={() => setCreateOrderOpen(false)}
        onSuccess={(order) => {
          setCreateOrderOpen(false);
          navigate(`/orders/${order.id}`);
        }}
      />
      <MoveToProductionDialog
        open={orderToMove !== null}
        onClose={() => setOrderToMove(null)}
        order={orderToMove}
        onSuccess={load}
      />
      <ReleaseOrderDialog
        open={orderToRelease !== null}
        onClose={() => setOrderToRelease(null)}
        order={orderToRelease}
        onSuccess={load}
      />
      <UndoReleaseDialog
        open={orderToUndoRelease !== null}
        onClose={() => setOrderToUndoRelease(null)}
        order={orderToUndoRelease}
        onSuccess={load}
      />
      <CancelOrderDialog
        open={orderToCancel !== null}
        onClose={() => setOrderToCancel(null)}
        order={orderToCancel}
        onSuccess={load}
      />
      <UndoCancelDialog
        open={orderToUndoCancel !== null}
        onClose={() => setOrderToUndoCancel(null)}
        order={orderToUndoCancel}
        onSuccess={load}
      />
      {printView}
      {attestationDialog}
    </>
  );
};

export default Orders;
